import * as React from 'react';
import { c } from '../../../utils/tailwind-merge';
import { TextareaProps } from './Textarea.types';

const Textarea = React.forwardRef<
  HTMLTextAreaElement,
  React.ComponentProps<'textarea'> & TextareaProps
>(
  (
    {
      className = '',
      dark = false,
      showRemainingChars = false,
      remainingCharsClassName = '',
      wrapperClassName = '',
      maxLength,
      value,
      ...props
    },
    ref
  ) => {
    const isWrapped = showRemainingChars && maxLength;

    const defaultStyles = `caret-blue-hover leading-6 text-base tracking-tighter rounded-lg border px-3 py-2`;
    const sizeStyles = isWrapped ? 'flex-1' : 'h-28 w-56';

    const lightStyles = `
    border-[#999] text-grey-900 placeholder:text-[#999]
    placeholder:opacity-100
    hover:border-grey-700 hover:bg-grey-100
    read-only:pointer-events-none read-only:border-0 read-only:bg-grey-100 read-only:placeholder:text-grey-900
    focus:border-grey-700 focus:outline-none focus:ring-2 focus:ring-blue-hover focus:ring-offset-2 focus:bg-white
    disabled:border-0 disabled:bg-grey-100 disabled:!text-[#999]`;

    const darkStyles = `
    border-grey-500 text-white bg-grey-900
    hover:border-grey-600 hover:bg-grey-800
    read-only:pointer-events-none read-only:border-0 read-only:bg-grey-800 read-only:placeholder:text-grey-100
    focus:ring-blue-hover focus:ring-offset-grey-800 focus:bg-grey-900 focus:border-grey-700 focus:outline-none focus:ring-2 focus:ring-offset-2
    disabled:border-0 disabled:bg-grey-800 disabled:!text-grey-500`;

    const textareaElement = (
      <textarea
        ref={ref}
        data-slot="textarea"
        maxLength={maxLength}
        value={value}
        className={c(
          dark
            ? `${defaultStyles} ${sizeStyles} ${darkStyles}`
            : `${defaultStyles} ${sizeStyles} ${lightStyles}`,
          className
        )}
        {...props}
      />
    );

    if (isWrapped) {
      const currentLength =
        typeof value === 'string' || typeof value === 'number'
          ? String(value).length
          : 0;
      const remainingChars = Math.max(0, maxLength - currentLength);

      return (
        <div className={c('flex flex-col gap-0.5', wrapperClassName)}>
          {textareaElement}
          <p
            className={c(
              'pl-3 text-xs',
              dark ? 'text-grey-400' : 'text-grey-700',
              remainingCharsClassName
            )}
          >
            {`${remainingChars}/${maxLength}`}
          </p>
        </div>
      );
    }

    return textareaElement;
  }
);

Textarea.displayName = 'Textarea';

export { Textarea };
